import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/stone-masonry-steps-installation-montclair-nj.webp";
import wanaqueStepsImage from "@/assets/masonry-steps-repair-wanaque-passaic-county-nj.webp";
import northCaldwellStepsImage from "@/assets/masonry-steps-repair-north-caldwell-essex-county-nj.webp";
import pomptonPlainsImage from "@/assets/morris-county-masonry-pavers-pompton-plains-nj-2.webp";

const CitationLink = ({ href, num, text }: { href: string; num: number; text: string }) => (
  <>
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
      {text}
    </a>
    <sup>
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline ml-0.5">
        [{num}]
      </a>
    </sup>
  </>
);

const faqs = [
  {
    question: "Can I put new mortar over old mortar on my brick steps?",
    answer: "No. Old mortar must be ground out to at least 3/4 inch depth first. Layering new mortar over old joints does not create a proper bond and typically fails within one or two seasons, especially in North Jersey's freeze-thaw climate.",
  },
  {
    question: "How do I know if my steps need repair or full replacement?",
    answer: "If the mortar joints are crumbling but the concrete core and footing are still solid, repair may be enough. If the steps have shifted, pulled away from the house, or have structural cracks wider than 1/4 inch, replacement is the better long-term option.",
  },
  {
    question: "Can new concrete be poured over old concrete steps?",
    answer: "Yes, if the existing base is structurally sound. The surface must be clean, roughed up, and treated with a bonding agent. The overlay needs to be at least 2 inches thick to hold up in North Jersey's freeze-thaw climate.",
  },
  {
    question: "Does Flex Seal fix cracked concrete steps?",
    answer: "No. Flex Seal is a surface sealant, not a structural repair product. It may slow water entry temporarily but does not fix cracks caused by settling, frost heave, or a failed footing underneath.",
  },
  {
    question: "How long does a masonry step repair last in North Jersey?",
    answer: "A proper mortar replacement on structurally sound steps lasts 15 to 25 years. If the footing or concrete base is compromised, repairs tend to fail much sooner, and new steps become the better investment.",
  },
  {
    question: "Does homeowners insurance cover concrete step repair?",
    answer: "It depends on the cause of the damage. A sudden event like a tree falling on the steps may be covered. Normal wear, settling, and freeze-thaw damage are typically considered maintenance and are not covered under most policies. Check with your carrier for specifics.",
  },
];

const citations = [
  {
    num: 1,
    text: "Mason Contractors Association of America (MCAA) — Technical Resources",
    url: "https://masoncontractors.org/technical",
    description: "Construction best practices, repair methods, reinforcement standards, masonry certification.",
  },
  {
    num: 2,
    text: "Brick Industry Association (BIA) — Technical Notes on Brick Construction",
    url: "https://gobrick.com/resources/technical-notes",
    description: "Tuckpointing procedures, mortar selection, cold weather masonry, brick maintenance bulletins.",
  },
  {
    num: 3,
    text: "ASTM International — C270: Standard Specification for Mortar for Unit Masonry",
    url: "https://store.astm.org/standards/c270",
    description: "Five mortar types (M, S, N, O, K), proportion specifications, compressive strength requirements.",
  },
  {
    num: 4,
    text: "International Code Council (ICC) — International Residential Code",
    url: "https://iccsafe.org",
    description: "Foundation requirements (R403), stairway standards (R311.7), structural repair guidelines.",
  },
  {
    num: 5,
    text: "New Jersey DCA — Division of Codes and Standards",
    url: "https://nj.gov/dca/codes",
    description: "NJ Uniform Construction Code (N.J.A.C. 5:23), permit requirements, state amendments to IRC, local code enforcement.",
  },
];

const youtubeShorts = [
  {
    url: "https://www.youtube.com/embed/Z1lcOlPIbWI",
    title: "Masonry Steps Repair Video 1",
  },
  {
    url: "https://www.youtube.com/embed/YBv-bvrkguI",
    title: "Masonry Steps Repair Video 2",
  },
  {
    url: "https://www.youtube.com/embed/t2rG1WND0ug",
    title: "Masonry Steps Repair Video 3",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

export default function Repair() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Steps & Stoops Repair Totowa, NJ | Masonry Pro Construction</title>
        <meta property="og:description" content="Crumbling front steps? We repair and replace masonry steps across North Jersey. Mortar replacement, concrete patching, or full rebuild. Free on-site assessment." />
        <meta property="og:title" content="Steps & Stoops Repair Totowa, NJ | Masonry Pro Construction" />
        <meta
          name="description"
          content="Crumbling front steps? We repair and replace masonry steps across North Jersey. Mortar replacement, concrete patching, or full rebuild. Free on-site assessment."
        />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Masonry Steps & Stoops Repair",
            description: "Masonry steps and stoops repair across North Jersey. Mortar replacement, concrete patching, or full rebuild.",
            url: "/masonry-services/steps-stoops-repair",
            image: "https://masonry-pro.com/og/steps-stoops-repair.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/steps-stoops-repair.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/steps-stoops-repair.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />

        <main>
          {/* Hero Section */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Masonry repair services in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-3xl">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Steps & Stoops Repair in Totowa, NJ Serving North Jersey:{" "}
                  <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Fix It Right</span>
                </h1>
                <p style={{ color: 'rgba(225,231,239,0.85)', fontSize: '1.25rem', marginBottom: '2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Crumbling front steps are one of the most common calls we get across Bergen, Passaic, Morris, and Essex counties. The mortar breaks down, bricks come loose, and the concrete starts flaking off. Sometimes a repair is all you need. Other times, the damage goes much deeper than what you can see from the surface. We handle masonry steps repair in North Jersey, and we will tell you straight whether a fix makes sense or if new steps are the better move. Not every job needs a full tear-out, but some absolutely do.
                </p>
              </div>
            </div>
          </section>

          {/* Block 0: Crumbling Mortar — text left, heroImage right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Crumbling Mortar and Loose Brick Are the First Signs Your Steps Are Failing
                  </h2>
                  <div className="space-y-4 text-muted-foreground leading-relaxed">
                    <p>You notice it in the mortar first. Small cracks between the bricks, a chunk that pops out when you nudge it with your foot. Mortar is intentionally softer than brick. It takes the beating, so the brick does not have to. But once the joints start failing, water gets in behind the face. That is when things move fast. One winter in North Jersey, a small mortar issue can become loose bricks and a crumbling step face. Step repair is one of the most common calls we get for <Link to="/" className="text-secondary hover:underline">masonry services in Totowa</Link>.</p>
                    <p>Older homes in Montclair, Ridgewood, Nutley, and Glen Ridge tend to have original mortar from the 1960s and 70s. After 30 to 50 years, that mortar has done its job and reached end of life. If the concrete core and footing underneath are still solid, mortar replacement can restore the bond and buy you more time. Tuckpointing can sometimes work at that stage.</p>
                    <p>However, if the brick is spalling, or the base concrete is cracked through, or the steps have shifted off the house, patching the mortar joints is just covering up a much bigger problem. You have to look at what is actually failing. Is it the joints, the brick face, or the structure underneath? Each one tells you something different about what your steps actually need.</p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img
                    src={heroImage}
                    alt="Stone masonry steps installation in Montclair, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Block 1: Why New Mortar Over Old Doesn't Hold — wanaqueStepsImage left, text right */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-square rounded-xl overflow-hidden border border-border lg:order-first order-last">
                  <img
                    src={wanaqueStepsImage}
                    alt="Before and after brick masonry steps repair — crumbling steps demolished and rebuilt with new brick and concrete in North Jersey by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="lg:order-last order-first">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Why Putting New Mortar Over Old Mortar Does Not Hold Up
                  </h2>
                  <div className="space-y-4 text-muted-foreground leading-relaxed">
                    <p>This one comes up all the time. Can you just smear new mortar over the old stuff and call it a day? No, it does not bond correctly. The old mortar must be ground out to a minimum depth of 3/4 inch before new material can bond to fresh masonry. The BIA is clear on this. A skim coat over weathered joints looks fine for about a month. Then it pops out, usually right around the first hard freeze.</p>
                    <p>Type S mortar is the standard for exterior step work. It meets at least 1,800 psi of compressive strength per ASTM C270 and is moisture-resistant. The new mortar gets packed into the cleaned joints and tooled to match the existing profile. That tooling is not cosmetic. A properly shaped joint sheds water instead of holding it. In a freeze-thaw climate, that is the difference between joints that last 20 years and joints that fail in 2.</p>
                    <p>Road salt accelerates mortar degradation, which is worth knowing if your home is located along a main route in Passaic or Bergen County. Steps that face north or sit in shade stay wet longer and deteriorate faster than south-facing entries. And if the joints are failing across the entire step structure, not just a few spots, mortar replacement alone might not be cost-effective. At that point, new steps with a proper footing underneath are the smarter investment.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Block 2: Pouring New Concrete Over Old — text left, northCaldwellImage right */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Pouring New Concrete Over Old Steps Only Works If the Base Is Sound
                  </h2>
                  <div className="space-y-4 text-muted-foreground leading-relaxed">
                    <p>Many homeowners in <Link to="/service-areas/passaic-county/clifton" className="text-secondary hover:underline">Clifton</Link>, Paterson, and <Link to="/service-areas/passaic-county/passaic" className="text-secondary hover:underline">Passaic</Link> ask about this. The steps look rough on top but seem solid enough underneath. Can you pour a new layer over what is already there? Sometimes, but only under the right conditions.</p>
                    <p>The existing footing and concrete core must remain structurally intact. No major cracks running through the depth. No settling or shifting. No separation from the house. If all that checks out, an overlay can work. The old surface is cleaned, roughened, and coated with a bonding agent. Then the new concrete is placed at a minimum thickness of 2 inches. Anything thinner than that cracks and peels off in a North Jersey winter. It is not a matter of if, it is when.</p>
                    <p>Quikrete frequently comes up in these conversations. It is a brand that makes both concrete mixes and mortar mixes. They are not the same product. Quikrete sells bonding agents for overlays, but surface preparation matters more than the brand name on the bag. And if the footing has failed or the steps have settled, no overlay is going to fix that. Many homes in Passaic County and northern Essex County have poured-concrete steps from the 1960s through the 1980s. Some still have solid footings. Many do not. The only way to know is to have it assessed before any repair work starts.</p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border">
                  <img
                    src={northCaldwellStepsImage}
                    alt="Newly repaired masonry steps leading to a red door in North Caldwell, Essex County, NJ"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Block 3: Most Step Repairs Need Full Replacement — pomptonPlainsImage left, text right */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-border lg:order-first order-last">
                  <img
                    src={pomptonPlainsImage}
                    alt="Outdoor patio with masonry pavers and brick steps in Pompton Plains, Morris County, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="lg:order-last order-first">
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Most Step Repairs in North Jersey End Up Needing Full Replacement
                  </h2>
                  <div className="space-y-4 text-muted-foreground leading-relaxed">
                    <p>We would rather give you the honest answer up front than take money for a repair that is not going to hold. If the footing has shifted, the steps have pulled away from the house, or the concrete core is crumbling all the way through, a surface repair does not solve the problem. Structural cracks wider than 1/4 inch that run through the full depth of the step? That is a replacement.</p>
                    <p>Surface spalling and minor mortar loss on an otherwise solid set of steps can sometimes be repaired. But that is a smaller percentage of the calls we get than people think. Most of the steps we look at in North Jersey have been failing slowly for years by the time someone picks up the phone. And a repair on a structure that is already compromised just delays the inevitable. You end up paying for the repair now and the replacement later.</p>
                    <p>New steps with a footing below the 36-inch frost line, rebar reinforcement, and Type S mortar last 30 to 50 years. We look at the footing and the base before we recommend anything. Hilly areas in Morris County and western Bergen County are especially prone to shifting because of slope drainage. Root pressure from mature trees moves footings too. If a repair makes sense for your situation, we will tell you. But if the foundation is gone, new steps are the right call and we are not going to pretend otherwise.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Block 4: DIY Patch Products — stacked layout */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    DIY Patch Products Do Not Fix Structural Step Problems
                  </h2>
                  <div className="space-y-4 text-muted-foreground leading-relaxed">
                    <p>Every spring, the hardware stores in Clifton, Paterson, and Nutley sell a lot of Flex Seal, magic crack filler, and concrete patch. Winter does a number on steps around here and people want a quick fix. Fair enough. But those products are surface sealants. They are not structural repairs. Flex Seal can slow water from getting into a hairline crack on a flat slab. It does not restore the bond between masonry units on a set of steps that are separating.</p>
                    <p>Cracks caused by a failed footing, frost heave, or settling keep growing underneath whatever you put on top. The patch holds for a while, then the crack comes back wider than before. That is not the product failing. It is the structure underneath continuing to move. No tube or spray can from a shelf is rated to fix that.</p>
                    <p>A couple of terms come up in online searches that are worth clearing up. The 1/2/3 rule is a concrete mix ratio. One part cement, 2 parts sand, 3 parts gravel. It is a guideline for mixing concrete, not a repair method. Proper step repair means cutting out what has failed, figuring out why it failed, and rebuilding with the right materials. If the cause is a bad footing or structural settling, the fix starts underground.</p>
                  </div>
                </div>
                <Card className="border-secondary/40 bg-secondary/5">
                  <CardHeader>
                    <CardTitle className="text-xl md:text-2xl">
                      Can You Put New Concrete Over Old Concrete Steps?
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-muted-foreground">
                      It depends on the condition of the existing steps. If the base concrete is structurally sound with no major cracks or shifting, a bonded overlay can work. If the footing has failed or the steps have pulled away from the house, a surface layer will not hold.
                    </p>
                    <ul className="space-y-3">
                      {[
                        "The old surface must be clean, rough, and free of loose material",
                        "A bonding agent or slurry coat is required for adhesion",
                        "Overlays under 2 inches thick are prone to cracking and delamination in freeze-thaw climates",
                      ].map((bullet, index) => (
                        <li key={index} className="flex items-start gap-3 text-muted-foreground">
                          <span className="text-secondary mt-1 font-bold">•</span>
                          {bullet}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          <YouTubeShortsSection 
            title="See Our"
            highlightedTitle="Repair Work"
            videoIds={["Z1lcOlPIbWI", "YBv-bvrkguI", "t2rG1WND0ug"]}
          />

          {/* FAQ Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Frequently Asked <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <Card key={index} className="h-full">
                    <CardHeader>
                      <CardTitle className="text-lg leading-snug">{faq.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <RelatedServices currentCategory="masonry" />

          {/* CTA Section */}
          <CTASection
            serviceName="Masonry Steps Repair"
            heading="Fix Your Steps"
            highlightedHeading="Before They Get Worse"
          />

          {/* Citations Section */}
          <section className="py-12 bg-card border-t border-border">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible>
                  <AccordionItem value="citations" className="border-none">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      References & Citations
                    </AccordionTrigger>
                    <AccordionContent>
                      <ol className="space-y-4 mt-4">
                        {citations.map((citation) => (
                          <li key={citation.num} className="text-sm">
                            <CitationLink href={citation.url} num={citation.num} text={citation.text} />
                            <span className="text-muted-foreground ml-2">— {citation.description}</span>
                          </li>
                        ))}
                      </ol>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
